// src/posts/RingAllReduce.jsx
// Scaling Training · Part 3 of 5 — Ring All-Reduce.
// TAGS FOR REGISTRATION: ['training', 'distributed', 'communication']
// EXCERPT: Where does the "2Ψ on the wire" number in DDP and ZeRO actually come from? Ring all-reduce. N-1 reduce-scatter rounds plus N-1 all-gather rounds, each chunk hopping clockwise around the cluster. Bandwidth-optimal, and the workhorse of every modern training run.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import usePageMeta from '../usePageMeta';
import './PostChrome.css';
import './RingAllReduce.css';

/* ============================================================
   KaTeX wrapper
   ============================================================ */
function Katex({ tex, block = false, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current) return;
      if (window.katex) {
        try {
          window.katex.render(tex, ref.current, { displayMode: block, throwOnError: false });
        } catch {
          if (ref.current) ref.current.textContent = tex;
        }
      } else {
        setTimeout(render, 60);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [tex, block]);
  return block
    ? <div ref={ref} className={`rar-katex-block ${className}`} />
    : <span ref={ref} className={`rar-katex-inline ${className}`} />;
}

/* ============================================================
   Build the 2(N-1)+1 frames of ring all-reduce.
   cells[frame][gpu][slot] is a bitmask of which GPUs' originals
   have been summed into that cell.
   ============================================================ */
function buildFrames(N) {
  const init = Array.from({ length: N }, (_, i) => Array.from({ length: N }, () => 1 << i));
  const frames = [init];

  // Reduce-scatter: at step k (1..N-1), GPU i sends slot ((i - k + 1) mod N) clockwise.
  for (let k = 1; k <= N - 1; k++) {
    const prev = frames[frames.length - 1];
    const next = prev.map((row) => [...row]);
    for (let i = 0; i < N; i++) {
      const slot = ((i - k + 1) % N + N) % N;
      const receiver = (i + 1) % N;
      next[receiver][slot] = prev[receiver][slot] | prev[i][slot];
    }
    frames.push(next);
  }

  // All-gather: at step k (1..N-1), GPU i sends slot ((i - k + 2) mod N) clockwise.
  for (let k = 1; k <= N - 1; k++) {
    const prev = frames[frames.length - 1];
    const next = prev.map((row) => [...row]);
    for (let i = 0; i < N; i++) {
      const slot = ((i - k + 2) % N + N) % N;
      const receiver = (i + 1) % N;
      next[receiver][slot] = prev[i][slot];
    }
    frames.push(next);
  }

  return frames;
}

function popcount(x) { let c = 0; while (x) { c += x & 1; x >>>= 1; } return c; }
function letters(mask, N) {
  const L = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  const out = [];
  for (let i = 0; i < N; i++) if (mask & (1 << i)) out.push(L[i]);
  return out;
}
function cellLabel(mask, N) {
  if (popcount(mask) === N) return 'Σ';
  return letters(mask, N).join('+');
}

const GPU_COLORS = [
  'hsl(218, 70%, 50%)',
  'hsl(35, 85%, 55%)',
  'hsl(280, 50%, 55%)',
  'hsl(148, 55%, 42%)',
];
function cellFill(mask, N) {
  const c = popcount(mask);
  if (c === N) return '#1e293b';
  if (c === 1) {
    // The single owner — color by bit index
    for (let i = 0; i < N; i++) if (mask & (1 << i)) return GPU_COLORS[i];
  }
  // Partial sum: lighten with mix
  return `hsl(220, 14%, ${82 - c * 8}%)`;
}

/* ============================================================
   Build the frames of recursive halving / doubling for N (power of 2).
   Reduce-scatter (halving): distance starts at N/2, halves each round.
   All-gather (doubling):    distance starts at 1, doubles each round.

   cells[frame][gpu][slot] = { mask, owned }
     mask  = bitmask of contributors
     owned = whether this GPU is currently "responsible for" the chunk
   ============================================================ */
function buildHalvingFrames(N) {
  const initial = Array.from({ length: N }, (_, i) =>
    Array.from({ length: N }, () => ({ mask: 1 << i, owned: true })),
  );
  const frames = [{ cells: initial, distance: null, phase: 'init' }];

  // Reduce-scatter — halving phase
  for (let d = N / 2; d >= 1; d /= 2) {
    const prev = frames[frames.length - 1].cells;
    const next = prev.map((row) => row.map((c) => ({ ...c })));
    for (let i = 0; i < N; i++) {
      const partner = i ^ d;
      const iBit = (i & d) !== 0;
      for (let c = 0; c < N; c++) {
        const cBit = (c & d) !== 0;
        if (iBit === cBit) {
          // GPU i keeps this chunk; reduce in partner's value
          if (prev[i][c].owned && prev[partner][c].owned) {
            next[i][c] = {
              mask: prev[i][c].mask | prev[partner][c].mask,
              owned: true,
            };
          }
        } else {
          // GPU i gives this chunk away
          if (prev[i][c].owned) {
            next[i][c] = { mask: prev[i][c].mask, owned: false };
          }
        }
      }
    }
    frames.push({ cells: next, distance: d, phase: 'rs' });
  }

  // All-gather — doubling phase
  for (let d = 1; d <= N / 2; d *= 2) {
    const prev = frames[frames.length - 1].cells;
    const next = prev.map((row) => row.map((c) => ({ ...c })));
    for (let i = 0; i < N; i++) {
      const partner = i ^ d;
      for (let c = 0; c < N; c++) {
        if (!prev[i][c].owned && prev[partner][c].owned) {
          next[i][c] = { mask: prev[partner][c].mask, owned: true };
        }
      }
    }
    frames.push({ cells: next, distance: d, phase: 'ag' });
  }

  return frames;
}

function HalvingWidget() {
  const N = 4;
  const frames = useMemo(() => buildHalvingFrames(N), []);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setFrame((f) => {
        if (f + 1 >= frames.length) { setPlaying(false); return f; }
        return f + 1;
      });
    }, 1100);
    return () => clearInterval(timerRef.current);
  }, [playing, frames.length]);

  const cur = frames[frame];
  const logN = Math.log2(N);
  const totalRounds = 2 * logN; // log N rs + log N ag
  const phaseLabel =
    cur.phase === 'init' ? 'initial'
    : cur.phase === 'rs' ? `reduce-scatter · distance ${cur.distance}`
    : `all-gather · distance ${cur.distance}`;
  const phaseClass =
    cur.phase === 'init' ? 'init' : cur.phase === 'rs' ? 'rs' : 'ag';

  // Cumulative bytes sent (per GPU) up to and including this frame.
  // RS halving sends M/2, M/4, ..., M/N (sum = M(N-1)/N)
  // AG doubling sends M/N, 2M/N, ..., M/2 (sum = M(N-1)/N)
  const bytesByFrame = (() => {
    const arr = [0];
    let total = 0;
    for (let r = 1; r <= logN; r++) total += 1 / Math.pow(2, r);
    let acc = 0;
    for (let r = 1; r <= logN; r++) {
      acc += 1 / Math.pow(2, r);
      arr.push(acc);
    }
    for (let r = 1; r <= logN; r++) {
      acc += Math.pow(2, r - 1) / N;
      arr.push(acc);
    }
    return arr;
  })();
  const cumBytes = bytesByFrame[frame];
  const maxBytes = 2 * (N - 1) / N;

  const W = 760, H = 360;
  const padX = 36, padTop = 30, padBot = 70;
  const innerW = W - 2 * padX;
  const innerH = H - padTop - padBot;
  const colGap = 18;
  const gpuW = (innerW - colGap * (N - 1)) / N;
  const gpuH = innerH - 28;
  const cellGap = 4;
  const cellH = (gpuH - cellGap * (N + 1) - 22) / N;

  const partnerOf = (i) => cur.distance !== null ? (i ^ cur.distance) : null;
  // Center x for GPU i in the layout
  const gpuCenterX = (gi) => padX + gi * (gpuW + colGap) + gpuW / 2;

  return (
    <div className="viz-panel rar-widget">
      <div className="rar-controls">
        <div className={`rar-phase rar-phase-${phaseClass}`}>{phaseLabel}</div>
        <div className="rar-buttons">
          <button onClick={() => { setPlaying(false); setFrame(0); }} className="rar-btn">⟲ Reset</button>
          <button onClick={() => { setPlaying(false); setFrame((f) => Math.max(0, f - 1)); }}
                  className="rar-btn" disabled={frame === 0}>‹ Prev</button>
          <button onClick={() => setPlaying((p) => !p)} className="rar-btn rar-btn-primary">
            {playing ? '❚❚ Pause' : '▶ Play'}
          </button>
          <button onClick={() => { setPlaying(false); setFrame((f) => Math.min(frames.length - 1, f + 1)); }}
                  className="rar-btn" disabled={frame === frames.length - 1}>Next ›</button>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="rar-svg" preserveAspectRatio="xMidYMid meet">
        {/* GPU columns */}
        {Array.from({ length: N }).map((_, gi) => {
          const x = padX + gi * (gpuW + colGap);
          const headerY = padTop;
          return (
            <g key={gi}>
              <rect x={x} y={headerY} width={gpuW} height={gpuH} rx={8}
                    fill="white" stroke="var(--post-border)" />
              <text x={x + gpuW / 2} y={headerY + 16} textAnchor="middle"
                    fontSize="12" fontFamily="var(--post-mono)"
                    fontWeight="600" fill="var(--post-text)">GPU {gi}</text>
              <rect x={x} y={headerY} width={4} height={gpuH} rx={2}
                    fill={GPU_COLORS[gi]} />
              {Array.from({ length: N }).map((_, ci) => {
                const cy = headerY + 22 + cellGap + ci * (cellH + cellGap);
                const cx = x + 10;
                const cw = gpuW - 20;
                const cell = cur.cells[gi][ci];
                const isReduced = popcount(cell.mask) === N;
                const isOwned = cell.owned;
                const label = isOwned ? cellLabel(cell.mask, N) : '·';
                let fill = isOwned ? cellFill(cell.mask, N) : '#f1f5f9';
                let stroke = isOwned ? (isReduced ? '#1e293b' : 'var(--post-border)') : 'var(--post-border)';
                let strokeDash = isOwned ? undefined : '3 3';
                let textFill = isOwned && (popcount(cell.mask) >= 2 || isReduced) ? '#fff' : (isOwned ? '#1e293b' : 'var(--post-text-mut)');
                return (
                  <g key={ci}>
                    <rect x={cx} y={cy} width={cw} height={cellH} rx={4}
                          fill={fill} stroke={stroke} strokeDasharray={strokeDash} />
                    <text x={cx + cw / 2} y={cy + cellH / 2 + 4}
                          textAnchor="middle"
                          fontSize={cellH > 36 ? 14 : 12}
                          fontFamily="var(--post-mono)" fontWeight="600"
                          fill={textFill}>
                      {label}
                    </text>
                    <text x={cx + 4} y={cy + 10}
                          textAnchor="start" fontSize="9"
                          fontFamily="var(--post-mono)"
                          fill={isOwned && (popcount(cell.mask) >= 2 || isReduced) ? 'rgba(255,255,255,0.7)' : 'var(--post-text-mut)'}>
                      slot {ci}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* partner arcs — show this round's pairings under the GPUs */}
        {cur.distance !== null && (
          <g>
            {Array.from({ length: N / 2 }).map((_, idx) => {
              // Each pair drawn once: pick GPUs where i < partner
              const pairs = [];
              const seen = new Set();
              for (let i = 0; i < N; i++) {
                if (seen.has(i)) continue;
                const p = i ^ cur.distance;
                if (p < N && !seen.has(p)) {
                  pairs.push([i, p]);
                  seen.add(i); seen.add(p);
                }
              }
              const [a, b] = pairs[idx];
              if (a === undefined) return null;
              const x1 = gpuCenterX(a);
              const x2 = gpuCenterX(b);
              const yBase = padTop + gpuH + 12;
              const arcDepth = 18 + Math.abs(b - a) * 6;
              return (
                <g key={idx}>
                  <path
                    d={`M ${x1} ${yBase} Q ${(x1 + x2) / 2} ${yBase + arcDepth}, ${x2} ${yBase}`}
                    fill="none"
                    stroke={cur.phase === 'rs' ? 'hsl(35, 85%, 45%)' : 'hsl(148, 55%, 38%)'}
                    strokeWidth="1.6"
                    strokeDasharray={cur.phase === 'rs' ? undefined : '4 4'}
                  />
                  <circle cx={x1} cy={yBase} r="3"
                          fill={cur.phase === 'rs' ? 'hsl(35, 85%, 45%)' : 'hsl(148, 55%, 38%)'} />
                  <circle cx={x2} cy={yBase} r="3"
                          fill={cur.phase === 'rs' ? 'hsl(35, 85%, 45%)' : 'hsl(148, 55%, 38%)'} />
                </g>
              );
            })}
            <text x={W / 2} y={padTop + gpuH + 60} textAnchor="middle"
                  fontSize="11" fontFamily="var(--post-mono)"
                  fill="var(--post-text-mid)">
              partners this round (distance {cur.distance})
            </text>
          </g>
        )}
      </svg>

      <div className="rar-kpis">
        <div className="rar-kpi">
          <div className="rar-kpi-label">Round</div>
          <div className="rar-kpi-val">{frame} / {totalRounds}</div>
          <div className="rar-kpi-sub">
            {cur.phase === 'init' && <>each GPU has its own full tensor</>}
            {cur.phase === 'rs' && <>halve the active region · pair at distance {cur.distance}</>}
            {cur.phase === 'ag' && <>double the active region · pair at distance {cur.distance}</>}
          </div>
        </div>
        <div className="rar-kpi">
          <div className="rar-kpi-label">Bytes sent per GPU</div>
          <div className="rar-kpi-val">{cumBytes.toFixed(2)} <span className="rar-kpi-tail">M</span></div>
          <div className="rar-kpi-sub">
            cap at <Katex tex={`2(N-1)/N \\cdot M = ${maxBytes.toFixed(2)} \\, M`} /> · same as ring
          </div>
        </div>
      </div>
      <p className="rar-caption">
        Same 4 GPUs, same end state — but only <Katex tex="2\log_2 N = 4" />{' '}
        rounds instead of 6. Each round pairs every GPU with a partner at a
        specific power-of-two distance (arcs underneath). Cells the GPU
        is currently responsible for are coloured solid; cells it has
        handed off appear dashed/grey. Reduce-scatter halves the active
        region each round; all-gather doubles it back up.
      </p>
    </div>
  );
}

function RingAllReduceWidget() {
  const N = 4;
  const frames = useMemo(() => buildFrames(N), []);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setFrame((f) => {
        if (f + 1 >= frames.length) { setPlaying(false); return f; }
        return f + 1;
      });
    }, 900);
    return () => clearInterval(timerRef.current);
  }, [playing, frames.length]);

  const isInitial = frame === 0;
  const isRS = frame >= 1 && frame <= N - 1;
  const isAG = frame >= N && frame <= 2 * (N - 1);
  const phaseLabel = isInitial ? 'initial' : isRS ? `reduce-scatter ${frame}/${N - 1}` : `all-gather ${frame - (N - 1)}/${N - 1}`;
  const phaseClass = isInitial ? 'init' : isRS ? 'rs' : 'ag';

  // Comm cost: per round, each GPU sends one chunk = M/N bytes.
  // After r rounds (out of 2(N-1) total), per-GPU send = r · M/N.
  const totalRounds = 2 * (N - 1);
  const ringSentPerGpu = (frame / N).toFixed(2); // in units of M (per chunk = 1/N of M)

  const W = 760, H = 360;
  const padX = 36, padTop = 30, padBot = 64;
  const innerW = W - 2 * padX;
  const innerH = H - padTop - padBot;
  const colGap = 18;
  const gpuW = (innerW - colGap * (N - 1)) / N;
  const gpuH = innerH;
  const cellGap = 4;
  const cellH = (gpuH - cellGap * (N + 1) - 22) / N; // leave 22 for header

  const cells = frames[frame];

  return (
    <div className="viz-panel rar-widget">
      <div className="rar-controls">
        <div className={`rar-phase rar-phase-${phaseClass}`}>{phaseLabel}</div>
        <div className="rar-buttons">
          <button onClick={() => { setPlaying(false); setFrame(0); }} className="rar-btn">⟲ Reset</button>
          <button onClick={() => { setPlaying(false); setFrame((f) => Math.max(0, f - 1)); }}
                  className="rar-btn" disabled={frame === 0}>‹ Prev</button>
          <button onClick={() => setPlaying((p) => !p)} className="rar-btn rar-btn-primary">
            {playing ? '❚❚ Pause' : '▶ Play'}
          </button>
          <button onClick={() => { setPlaying(false); setFrame((f) => Math.min(frames.length - 1, f + 1)); }}
                  className="rar-btn" disabled={frame === frames.length - 1}>Next ›</button>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="rar-svg" preserveAspectRatio="xMidYMid meet">
        {/* Ring arrow at the bottom */}
        <g className="rar-ring-arrow">
          {Array.from({ length: N }).map((_, i) => {
            const x1 = padX + i * (gpuW + colGap) + gpuW / 2;
            const y1 = padTop + gpuH + 18;
            const next = (i + 1) % N;
            const isWrap = next === 0 && i === N - 1;
            const x2 = isWrap
              ? padX + 0 * (gpuW + colGap) + gpuW / 2
              : padX + next * (gpuW + colGap) + gpuW / 2;
            const y2 = y1;
            return (
              <g key={`a-${i}`}>
                {isWrap ? (
                  <>
                    {/* wrap curve under all GPUs */}
                    <path
                      d={`M ${x1} ${y1} Q ${x1 + 18} ${y1 + 30}, ${(W) / 2} ${y1 + 30} T ${x2 - 18} ${y1 + 30} Q ${x2 - 18} ${y1}, ${x2} ${y1}`}
                      fill="none" stroke="var(--post-border)" strokeWidth="1.4" strokeDasharray="4 4"
                    />
                  </>
                ) : (
                  <line x1={x1 + 8} y1={y1} x2={x2 - 8} y2={y2}
                        stroke="var(--post-border)" strokeWidth="1.4" markerEnd="url(#rar-arrow)" />
                )}
              </g>
            );
          })}
          <defs>
            <marker id="rar-arrow" viewBox="0 0 10 10" refX="8" refY="5"
                    markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--post-text-mut)" />
            </marker>
          </defs>
        </g>

        {/* GPU columns */}
        {Array.from({ length: N }).map((_, gi) => {
          const x = padX + gi * (gpuW + colGap);
          const headerY = padTop;
          return (
            <g key={gi}>
              <rect x={x} y={headerY} width={gpuW} height={gpuH} rx={8}
                    fill="white" stroke="var(--post-border)" />
              <text x={x + gpuW / 2} y={headerY + 16} textAnchor="middle"
                    fontSize="12" fontFamily="var(--post-mono)"
                    fontWeight="600" fill="var(--post-text)">GPU {gi}</text>
              {/* per-GPU border tag in own color */}
              <rect x={x} y={headerY} width={4} height={gpuH} rx={2}
                    fill={GPU_COLORS[gi]} />
              {/* cells */}
              {Array.from({ length: N }).map((_, ci) => {
                const cy = headerY + 22 + cellGap + ci * (cellH + cellGap);
                const cx = x + 10;
                const cw = gpuW - 20;
                const mask = cells[gi][ci];
                const fill = cellFill(mask, N);
                const isReduced = popcount(mask) === N;
                const label = cellLabel(mask, N);
                return (
                  <g key={ci}>
                    <rect x={cx} y={cy} width={cw} height={cellH} rx={4}
                          fill={fill} stroke={isReduced ? '#1e293b' : 'var(--post-border)'} />
                    <text x={cx + cw / 2} y={cy + cellH / 2 + 4}
                          textAnchor="middle"
                          fontSize={cellH > 36 ? 14 : 12}
                          fontFamily="var(--post-mono)" fontWeight="600"
                          fill={popcount(mask) >= 2 || isReduced ? '#fff' : '#1e293b'}>
                      {label}
                    </text>
                    <text x={cx + 4} y={cy + 10}
                          textAnchor="start" fontSize="9"
                          fontFamily="var(--post-mono)" fill={popcount(mask) >= 2 || isReduced ? 'rgba(255,255,255,0.7)' : 'var(--post-text-mut)'}>
                      slot {ci}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      <div className="rar-kpis">
        <div className="rar-kpi">
          <div className="rar-kpi-label">Round</div>
          <div className="rar-kpi-val">{frame} / {totalRounds}</div>
          <div className="rar-kpi-sub">
            {isInitial && <>each GPU has its own full tensor</>}
            {isRS && <>chunks rotate <em>and add</em></>}
            {isAG && <>chunks rotate <em>and overwrite</em></>}
          </div>
        </div>
        <div className="rar-kpi">
          <div className="rar-kpi-label">Bytes sent per GPU</div>
          <div className="rar-kpi-val">{ringSentPerGpu} <span className="rar-kpi-tail">M</span></div>
          <div className="rar-kpi-sub">
            cap at <Katex tex={`2(N-1)/N \\cdot M = ${(2 * (N - 1) / N).toFixed(2)} \\, M`} />
          </div>
        </div>
      </div>
      <p className="rar-caption">
        4 GPUs in a logical ring. Each starts with its own copy of the tensor split into 4
        chunks (<code>A</code>, <code>B</code>, <code>C</code>, <code>D</code> are GPU labels;{' '}
        <code>Σ</code> means "fully reduced — all 4 GPUs contributed"). The dashed loop at the
        bottom shows the send direction.
      </p>
    </div>
  );
}

/* ============================================================
   Page
   ============================================================ */
export default function RingAllReduce() {
  usePageMeta({
    title: 'Ring All-Reduce: How Collectives Actually Move Bytes',
    description: 'Where does the "2Ψ on the wire" number in DDP and ZeRO come from? Ring all-reduce: N-1 rounds of reduce-scatter plus N-1 of all-gather, with each chunk hopping clockwise around the cluster.',
    slug: 'ring-all-reduce',
    publishedDate: '2026-06-18',
    keywords: ['ring all-reduce', 'NCCL', 'MPI', 'collectives', 'distributed training', 'data parallel'],
  });

  return (
    <article className="post-2026 rar-post">
      <div className="post-wrap">
        <div className="post-hero">
          <div className="post-series-tag">
            <span className="post-live-dot" aria-hidden="true" />Scaling Training · Part 3 of 5
          </div>
          <h1>Ring All-Reduce: How Collectives Actually Move Bytes</h1>
          <p className="post-lede">
            Every distributed training post drops the same number: gradient sync
            costs roughly <strong>2Ψ bytes per GPU per step</strong>. Why{' '}
            <em>that</em> number? Why not <Katex tex="N\Psi" /> for{' '}
            <Katex tex="N" /> GPUs, or <Katex tex="\Psi" />? The answer is one
            algorithm — <strong>ring all-reduce</strong> — and it's the reason
            you can train at all on hundreds of devices.
          </p>
          <div className="post-byline">
            By <strong>Bernhard Walser</strong> &amp;{' '}
            <a className="post-link" href="https://www.anthropic.com/claude" target="_blank" rel="noreferrer"><strong>Claude</strong></a>
            {' '}(Anthropic) · co-written and co-designed ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>

        <h2 className="reveal">The naive way doesn't scale</h2>
        <p>
          The job: every GPU starts the step with its own gradient vector{' '}
          <Katex tex="g_i" /> of size <Katex tex="M" /> bytes. By the end, every
          GPU should hold the same vector: the elementwise sum{' '}
          <Katex tex="g = \sum_i g_i" />.
        </p>
        <p>
          The textbook approach is "broadcast then reduce": pick one GPU as
          root, every other GPU sends it their vector, the root sums them, then
          broadcasts the result back. The root then sends and receives{' '}
          <Katex tex="(N-1)M" /> bytes — bandwidth blows up linearly with{' '}
          <Katex tex="N" />, and the root is the bottleneck. Try this on 256
          GPUs and you spend most of your training time waiting on one network
          card.
        </p>

        <h2 className="reveal">The ring trick</h2>
        <p>
          Ring all-reduce escapes both problems by{' '}
          <strong>distributing the work and the bandwidth across all
          GPUs at the same time</strong>. Arrange the <Katex tex="N" /> GPUs
          in a logical ring (GPU 0 → 1 → 2 → … → N−1 → 0). Split each gradient
          vector into <Katex tex="N" /> equal chunks of size{' '}
          <Katex tex="M/N" />. Then run two phases of{' '}
          <Katex tex="N-1" /> rounds each.
        </p>
        <p>
          <strong>Phase 1 — reduce-scatter.</strong> In each round, every GPU
          sends one chunk clockwise to its right neighbor, who{' '}
          <em>adds it</em> to their copy of that chunk. The chunks flow in a
          staggered pattern so that after <Katex tex="N-1" /> rounds, every
          GPU has one fully-reduced chunk — but a different chunk per GPU.
        </p>
        <p>
          <strong>Phase 2 — all-gather.</strong> Same ring, same direction, but
          this time the chunks <em>overwrite</em> instead of accumulate.
          After another <Katex tex="N-1" /> rounds every GPU has every
          fully-reduced chunk, which is exactly the all-reduce result.
        </p>
        <p>
          The widget steps through the algorithm for{' '}
          <Katex tex="N=4" />. Cells show which GPUs' originals have been
          summed into them; <code>Σ</code> means all four. Hit{' '}
          <em>Play</em> or step round by round.
        </p>
        <RingAllReduceWidget />

        <h2 className="reveal">Why it's <Katex tex="\approx 2M" /> on the wire</h2>
        <p>
          Each round, every GPU sends exactly one chunk of size{' '}
          <Katex tex="M/N" />. There are <Katex tex="2(N-1)" /> rounds total
          (reduce-scatter plus all-gather). So per GPU, total bytes sent =
        </p>
        <Katex
          block
          tex={'2(N-1) \\cdot \\frac{M}{N} \\;=\\; \\frac{2(N-1)}{N} M \\;\\xrightarrow[N \\to \\infty]{} \\; 2M.'}
        />
        <p>
          That's the <strong>2Ψ</strong> you keep seeing in DDP / ZeRO papers.
          The per-GPU bandwidth is essentially independent of cluster size — a
          1024-GPU run sends the same bytes per device as a 4-GPU run. That's
          why this scales.
        </p>

        <h2 className="reveal">Why this is also bandwidth-optimal</h2>
        <p>
          You can't do better than <Katex tex="\approx 2M" />, and the argument
          is short. Each GPU starts with <Katex tex="M" /> bytes of "its own"
          data and ends with the full sum — which is <Katex tex="M" /> bytes
          containing every other GPU's contribution. So each GPU must{' '}
          <em>receive</em> at least <Katex tex="(N-1)/N \cdot M" /> bytes (the
          chunks it doesn't own) just to learn the all-reduced result, and{' '}
          <em>send</em> at least <Katex tex="(N-1)/N \cdot M" /> bytes for
          everyone else to learn its own contribution. Sum: <Katex tex="2(N-1)/N \cdot M" />.
        </p>
        <p>
          Ring all-reduce hits this lower bound exactly. That's the result in{' '}
          <a className="post-link" href="https://www.cs.fsu.edu/~xyuan/paper/09jpdc.pdf" target="_blank" rel="noreferrer">
            Patarasuk &amp; Yuan 2009
          </a> — the modern collectives canon.
        </p>

        <h2 className="reveal">Recursive halving/doubling</h2>
        <p>
          If sequential rounds are the problem, the obvious fix is to do{' '}
          <em>fewer</em> rounds. <strong>Recursive halving/doubling</strong>{' '}
          (<a className="post-link" href="https://link.springer.com/chapter/10.1007/978-3-540-24685-5_1" target="_blank" rel="noreferrer">Rabenseifner 2004</a>)
          uses a butterfly pattern: each GPU pairs with a partner at distance
          1, then 2, then 4, etc., doubling each round. After{' '}
          <Katex tex="\log_2 N" /> rounds of swap-and-reduce, the
          all-reduce is done.
        </p>
        <p>
          The reduce-scatter phase halves the "active region" each round —
          GPUs split their working set with their partner, keep half, send
          the other half — until each GPU owns one fully-reduced chunk. The
          all-gather phase reverses it: each round doubles the chunks each
          GPU holds, until everyone has the whole tensor.
        </p>
        <HalvingWidget />
        <p>
          For <Katex tex="N=4" />, that's <Katex tex="2 \log_2 4 = 4" />{' '}
          rounds versus 6 for the ring. For <Katex tex="N=512" /> it's 18
          rounds versus 1022 — the gap explodes as <Katex tex="N" /> grows.
          And the byte cost on each GPU is exactly the same:{' '}
          <Katex tex="2(N-1)/N \cdot M" />. So if you only look at the
          algorithmic analysis, halving/doubling looks strictly better.
        </p>

        <h2 className="reveal">So why isn't ring obsolete?</h2>
        <p>
          The algorithm-analysis answer says halving/doubling wins.
          Reality says you'll find ring in almost every production
          collective library. Four reasons stack up:
        </p>
        <ul className="rar-stack">
          <li>
            <strong>Ring pipelines naturally.</strong> Every round sends a
            uniform <Katex tex="M/N" />-sized chunk, in the same direction,
            independently of the others. While round <em>k</em> is on the
            wire, round <em>k+1</em>'s chunks can already be prepared.
            Halving/doubling has chunk sizes that <em>double</em> each
            round (<Katex tex="M/2, M/4, \ldots, M/N" />), so the comm
            pattern isn't uniform — much harder to pipeline.
          </li>
          <li>
            <strong>Network contention.</strong> This is the deeper reason —
            the original{' '}
            <a className="post-link" href="https://www.cs.fsu.edu/~xyuan/paper/09jpdc.pdf" target="_blank" rel="noreferrer">
              Patarasuk &amp; Yuan
            </a>{' '}
            paper makes it explicit. In butterfly,{' '}
            <em>all <Katex tex="N/2" /> swap-pairs run at the same
            instant</em>. On most real topologies (SMP/multi-core nodes,
            Ethernet fat trees, fat-tree InfiniBand) those pairs share
            physical links, and the simultaneous flows contend for the
            same uplink — effective bandwidth gets sliced up. Ring only
            sends in one direction at each step, so flows occupy disjoint
            links. Ring is bandwidth-optimal <em>in theory</em>;
            butterfly is bandwidth-optimal in theory but bandwidth-optimal
            <em> in practice</em> only on a true hypercube. Almost nobody
            has a true hypercube.
          </li>
          <li>
            <strong>Power-of-two only.</strong> Recursive halving/doubling
            natively needs <Katex tex="N = 2^k" />. For other{' '}
            <Katex tex="N" /> you pad with virtual nodes or fall back to a
            more complex variant; ring works for any <Katex tex="N" />.
          </li>
          <li>
            <strong>Most large-model training is bandwidth-bound.</strong>{' '}
            When <Katex tex="M/\beta \gg N\alpha" /> the latency term
            doesn't matter and both algorithms cost about the same. A
            gradient bucket for a billion-parameter model is a few hundred
            MB; on InfiniBand at 100 GB/s that's milliseconds of byte
            transfer against microseconds of per-round latency. Ring
            costs essentially the same in that regime, and you keep the
            implementation simplicity.
          </li>
        </ul>

        <h2 className="reveal">What NCCL actually does</h2>
        <p>
          Real systems use <strong>both</strong>. NCCL — NVIDIA's
          collective library — picks an algorithm based on message size
          and topology. A typical multi-node training run looks like:
        </p>
        <ul className="rar-stack">
          <li>
            <strong>Intra-node, large messages:</strong> ring all-reduce
            across NVLink (300+ GB/s). The topology argument doesn't
            really apply here — NVSwitch is all-to-all — but ring still
            wins on <em>pipelining</em>. Uniform <Katex tex="M/N" />{' '}
            chunks flow in one direction while reductions for the
            previous chunk happen in the other; NVLink is saturated
            continuously in both directions. Halving/doubling's
            doubling-chunk sizes can't pipeline that smoothly.
          </li>
          <li>
            <strong>Intra-node, small messages:</strong> tree or
            doubling-tree. Latency dominates at KB scale, so the{' '}
            <Katex tex="\log_2 N" /> round count matters even intra-node.
          </li>
          <li>
            <strong>Inter-node, small/medium messages:</strong> double
            binary tree (Sanders et al.) — gets both latency-optimal{' '}
            <Katex tex="\log_2 N" /> rounds <em>and</em>{' '}
            bandwidth-optimality by using two trees that share the
            cluster's links. Mellanox SHARP can also offload the
            reduction into the switch itself.
          </li>
          <li>
            <strong>Inter-node, large messages:</strong> still ring-shaped,
            but hierarchical — intra-node ring → ring across node-leaders
            over InfiniBand → intra-node broadcast. Pipelined throughout
            so both NVLink and IB stay saturated.
          </li>
          <li>
            <strong>Bucketing:</strong> gradients aren't all-reduced
            individually — PyTorch DDP groups them into ~25 MB buckets so
            the first all-reduce can start while the rest of backward is
            still running. Compute hides comm.
          </li>
        </ul>

        <h2 className="reveal">Caveats and edge cases</h2>
        <p>
          A few things to know.{' '}
          <strong>The ring assumes uniform bidirectional bandwidth</strong>{' '}
          between neighbors. On heterogeneous hardware (mixing PCIe and
          NVLink, or GPUs of different generations) NCCL falls back to other
          topologies and the elegant <Katex tex="2(N-1)/N \cdot M" /> bound
          frays.
        </p>
        <p>
          <strong>The ring serialises.</strong> The classical way to think
          about a collective's total cost is the{' '}
          <em>α-β model</em>:
        </p>
        <Katex
          block
          tex={'T \\;\\approx\\; \\alpha \\cdot (\\text{rounds}) \\;+\\; \\beta \\cdot (\\text{bytes})'}
        />
        <p>
          where <Katex tex="\alpha" /> is per-message latency (tens of
          microseconds inter-node) and <Katex tex="\beta" /> is inverse
          bandwidth. For ring all-reduce that becomes
        </p>
        <Katex
          block
          tex={'T_{\\text{ring}} \\;\\approx\\; \\underbrace{2(N-1)\\,\\alpha}_{\\text{grows with } N} \\;+\\; \\underbrace{\\tfrac{2M}{\\beta}}_{\\text{bounded}}.'}
        />
        <p>
          The byte term is exactly the
          bandwidth-optimal <Katex tex="2M" /> we just derived — it doesn't
          care how many GPUs are in the ring. But the latency term grows
          linearly with <Katex tex="N" />. At <Katex tex="N=4" /> the ring
          pays 6 round-trips; at <Katex tex="N=512" /> it pays 1022.
          Recursive halving/doubling pays the same byte cost but only{' '}
          <Katex tex="2\log_2 N" /> round-trips, so once{' '}
          <Katex tex="\alpha \cdot N" /> gets comparable to{' '}
          <Katex tex="M/\beta" /> it wins, decisively. That's also why
          super-large clusters use 2D / 3D / hypercube topologies instead
          of flat rings — fewer sequential hops in the critical path.
        </p>
        <p>
          <strong>Failure handling</strong> is its own world. If one GPU
          drops out mid-reduce-scatter, the ring stalls until the comm
          library times out. Production clusters add explicit health checks
          and gradient-bucket retries on top.
        </p>

        <h2 className="reveal">References</h2>
        <div className="post-refs">
          <div className="head ref-cite">Cite</div>
          <div className="head ref-title">Title</div>
          <div className="head ref-link">Link</div>

          <div className="ref-cite">Patarasuk &amp; Yuan 2009</div>
          <div>
            <div className="ref-title">
              <a href="https://www.cs.fsu.edu/~xyuan/paper/09jpdc.pdf" target="_blank" rel="noreferrer">Bandwidth Optimal All-reduce Algorithms for Clusters of Workstations</a>
            </div>
            <div className="ref-note">Proves the <Katex tex="2(N-1)/N \cdot M" /> lower bound and that ring all-reduce meets it.</div>
          </div>
          <div className="ref-link"><a href="https://www.cs.fsu.edu/~xyuan/paper/09jpdc.pdf" target="_blank" rel="noreferrer">cs.fsu.edu</a></div>

          <div className="ref-cite">Rabenseifner 2004</div>
          <div>
            <div className="ref-title">
              <a href="https://link.springer.com/chapter/10.1007/978-3-540-24685-5_1" target="_blank" rel="noreferrer">Optimization of Collective Reduction Operations</a>
            </div>
            <div className="ref-note">Recursive halving/doubling — the latency-optimal variant NCCL uses for small messages.</div>
          </div>
          <div className="ref-link"><a href="https://link.springer.com/chapter/10.1007/978-3-540-24685-5_1" target="_blank" rel="noreferrer">springer</a></div>

          <div className="ref-cite">NCCL docs</div>
          <div>
            <div className="ref-title">
              <a href="https://docs.nvidia.com/deeplearning/nccl/user-guide/docs/index.html" target="_blank" rel="noreferrer">NCCL Developer Guide</a>
            </div>
            <div className="ref-note">Topology detection, algorithm selection, and the production knobs.</div>
          </div>
          <div className="ref-link"><a href="https://docs.nvidia.com/deeplearning/nccl/user-guide/docs/index.html" target="_blank" rel="noreferrer">nvidia.com</a></div>

          <div className="ref-cite">Earlier posts</div>
          <div>
            <div className="ref-title">
              <a href="/blog/zero">ZeRO: Sharding the Optimizer</a>
            </div>
            <div className="ref-note">Where the 2Ψ comm volume first shows up in this series.</div>
          </div>
          <div className="ref-link"><a href="/blog/zero">/zero</a></div>
        </div>

        <footer className="post-footer">
          <p>
            <strong>Part 3 of Scaling Training</strong> · Previous:{' '}
            <a className="post-link" href="/blog/zero">ZeRO: Sharding the Optimizer</a>
            {' · '}Next:{' '}
            <a className="post-link" href="/blog/megatron">Megatron-LM: Tensor Parallelism</a>.
          </p>
          <p style={{ marginBottom: 0 }}>
            Bernhard Walser · ML Engineer, Digitec Galaxus · ETH Computer Science ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </p>
        </footer>
      </div>
    </article>
  );
}
